import React from 'react';
import styled from 'styled-components';

/**
 * Rating badge.
 *
 * Stars are SVG with a per-star clip so a 4.9 renders as four full stars plus a
 * 90%-filled fifth — the previous version floored to whole stars with a 50%
 * overflow:hidden span, so 4.9 and 4.5 looked identical and 4.9 lost a star.
 *
 * When `onClick` is supplied the whole badge becomes a button that opens the
 * reviews panel.
 */

const STAR_FULL = '#f59e0b';
const STAR_EMPTY_LIGHT = 'rgba(15, 23, 42, 0.15)';
const STAR_EMPTY_DARK = 'rgba(255, 255, 255, 0.22)';

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${p => (p.$clickable ? '4px 8px' : '0')};
  margin: ${p => (p.$clickable ? '0 -8px' : '0')};
  border-radius: 8px;
  border: none;
  background: transparent;
  font-family: inherit;
  cursor: ${p => (p.$clickable ? 'pointer' : 'default')};
  transition: background 0.15s ease;

  ${p => p.$clickable && `
    &:hover { background: rgba(245, 158, 11, 0.1); }
    &:focus-visible { outline: 2px solid ${STAR_FULL}; outline-offset: 2px; }
  `}
`;

const Score = styled.span`
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.95)' : '#0f172a')};
`;

const Stars = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 1px;
`;

const CountLabel = styled.span`
  font-size: 13px;
  line-height: 1;
  color: ${p => (p.$darkMode ? 'rgba(255,255,255,0.6)' : '#64748b')};
  text-decoration: ${p => (p.$clickable ? 'underline' : 'none')};
  text-underline-offset: 2px;
`;

const STAR_PATH =
  'M12 2.6l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.4 6.2 20.5l1.1-6.5L2.6 9.4l6.5-.9L12 2.6z';

/** One star filled 0..1 of the way across, clipped so partials are exact. */
const Star = ({ fill, size, darkMode }) => {
  const clipId = React.useMemo(
    () => `star-clip-${Math.round(fill * 100)}-${size}-${Math.random().toString(36).slice(2, 7)}`,
    [fill, size]
  );
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={24 * fill} height="24" />
        </clipPath>
      </defs>
      <path d={STAR_PATH} fill={darkMode ? STAR_EMPTY_DARK : STAR_EMPTY_LIGHT} />
      {fill > 0 && <path d={STAR_PATH} fill={STAR_FULL} clipPath={`url(#${clipId})`} />}
    </svg>
  );
};

const Rating = ({ rating, reviews, darkMode = false, size = 16, onClick, label }) => {
  const score = Number(rating) || 0;
  const count = Number(reviews) || 0;

  // Nothing to show yet — don't render an empty 0-star badge in the widget.
  if (!count) return null;

  const clickable = typeof onClick === 'function';

  return (
    <Container
      as={clickable ? 'button' : 'div'}
      type={clickable ? 'button' : undefined}
      $clickable={clickable}
      onClick={onClick}
      aria-label={
        clickable
          ? `${score} out of 5 from ${count} reviews. View all reviews.`
          : `${score} out of 5 from ${count} reviews`
      }
    >
      <Score $darkMode={darkMode}>{score.toFixed(1)}</Score>
      <Stars>
        {[0, 1, 2, 3, 4].map(i => (
          <Star key={i} fill={Math.max(0, Math.min(1, score - i))} size={size} darkMode={darkMode} />
        ))}
      </Stars>
      <CountLabel $darkMode={darkMode} $clickable={clickable}>
        {label || `(${count})`}
      </CountLabel>
    </Container>
  );
};

export default Rating;
